// One-line summaries for the six collapsed accordion groups on the mobile
// project detail (mobile overhaul Phase B §6, mockup §6.1 B). Pure
// formatters — the page hands in already-derived values and gets back either
// a string or a list of middot-separated parts, so the renderer can tint one
// clause (an overdue count in red, a crew total in gold, a tool status in
// accent) without the formatter knowing about React.

import { formatDayMonth } from './calendar';
import { formatMoney } from './moneyFormat';

/** One middot-separated clause of a summary line. `emphasis` trails `text`
    inside the same clause, always in accent — "Script Approved", "Social:
    Post soon". */
export interface SummaryPart {
  text: string;
  tone?: 'gold' | 'danger';
  emphasis?: string;
}

/** "22 Apr" · "22–23 Apr" · "28 Jun – 2 Jul" — an inclusive date range as
    tight as the two dates allow. */
export function dateRangeLabel(start: string, end?: string | null): string {
  if (!end || end === start) return formatDayMonth(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${Number(start.slice(8, 10))}–${formatDayMonth(end)}`;
  return `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
}

/** "Shoot · HSP · shoot 22–23 Apr · due 8 May". The edit range only earns a
    place when there is no shoot date — the line has to survive a 393px
    ellipsis, and the shoot is the date Henry looks for. */
export function statusDatesSummary(p: {
  phaseLabel: string;
  brand: string;
  shootDate: string | null;
  shootEndDate: string | null;
  editDate: string | null;
  editEndDate: string | null;
  dueDate: string | null;
}): string {
  const bits = [p.phaseLabel, p.brand.toUpperCase()];
  if (p.shootDate) bits.push(`shoot ${dateRangeLabel(p.shootDate, p.shootEndDate)}`);
  else if (p.editDate) bits.push(`edit ${dateRangeLabel(p.editDate, p.editEndDate)}`);
  if (p.dueDate) bits.push(`due ${formatDayMonth(p.dueDate)}`);
  return bits.join(' · ');
}

/** "$3,775 · 2 invoices · 1 overdue" — the quote in gold, the overdue count
    in red, so the collapsed group already says whether money is late. */
export function moneySummary(p: {
  quoteCents: number | null;
  invoiceCount: number;
  overdueCount: number;
}): SummaryPart[] {
  const parts: SummaryPart[] = [
    p.quoteCents === null ? { text: 'No quote' } : { text: formatMoney(p.quoteCents), tone: 'gold' },
    { text: invoiceCountLabel(p.invoiceCount) },
  ];
  if (p.overdueCount > 0) parts.push({ text: `${p.overdueCount} overdue`, tone: 'danger' });
  return parts;
}

/** "no invoices" · "1 invoice" · "2 invoices". */
export function invoiceCountLabel(count: number): string {
  if (count === 0) return 'no invoices';
  return `${count} invoice${count === 1 ? '' : 's'}`;
}

/** "Priya Nair · Macedon Ranges Glass" — the client alone when the project
    has resolved no contact at all. */
export function contactSummary(p: { contactName: string | null; clientName: string }): string {
  return p.contactName ? `${p.contactName} · ${p.clientName}` : p.clientName;
}

/** "James Staples, Mia Chen · $3,150" — names plain, total gold. A null
    total is the missing-half-rate state and reads as "—", as it does on the
    desktop rail. */
export function crewSummary(p: { names: string[]; totalCents: number | null }): SummaryPart[] {
  if (p.names.length === 0) return [{ text: 'No crew booked' }];
  return [{ text: p.names.join(', ') }, { text: formatMoney(p.totalCents), tone: 'gold' }];
}

/** "Script Approved · Proposal Signed · 2 delivery links" — only the tools
    that are actually linked, each with its status in accent. */
export function toolsSummary(p: {
  tools: { label: string; status: string | null }[];
  deliveryCount: number;
}): SummaryPart[] {
  const parts: SummaryPart[] = p.tools.map((t) =>
    t.status ? { text: t.label, emphasis: t.status } : { text: t.label },
  );
  if (parts.length === 0) parts.push({ text: 'No tools linked' });
  parts.push({ text: deliveryLinkLabel(p.deliveryCount) });
  return parts;
}

/** "No delivery links" · "1 delivery link" · "3 delivery links". */
export function deliveryLinkLabel(count: number): string {
  if (count === 0) return 'No delivery links';
  return `${count} delivery link${count === 1 ? '' : 's'}`;
}

/** "Not waiting · Social: Post soon" — the waiting clause turns gold once a
    client is holding the job up, and a social restriction is loud enough to
    earn the last clause in red. */
export function automationsSummary(p: {
  waitingDays: number | null;
  socialLabel: string | null;
  socialRestricted: boolean;
}): SummaryPart[] {
  const parts: SummaryPart[] = [
    p.waitingDays === null ? { text: 'Not waiting' } : { text: `Waiting ${p.waitingDays}d`, tone: 'gold' },
  ];
  if (p.socialLabel) parts.push({ text: 'Social:', emphasis: p.socialLabel });
  if (p.socialRestricted) parts.push({ text: 'No social use', tone: 'danger' });
  return parts;
}
